<script>
    import navigationService from '@/services/core/navigation.service';
    import { PermissionKey } from '@/enums/PermissionKey';

    export default {
        emits: ['toggle-sidebar'],
        computed: {
            getYear() {
                const date = new Date();
                return date.getFullYear();
            },
            getMenusItems() {
                const menuItems = [
                    {
                        group: 'Companies',
                        items: [
                            { name: 'Companies', link: '/companies', iconClass: 'bi-building', allowReadOnly: true },
                            {
                                name: 'Billing',
                                href: 'https://billing.greasebook.com',
                                iconClass: 'bi-receipt',
                                permissionKey: PermissionKey.Billing
                            },
                            {
                                name: 'Demo Sync Settings',
                                link: '/demo-settings',
                                iconClass: 'bi-sliders',
                                permissionKey: PermissionKey.Demo
                            }
                        ]
                    },
                    {
                        group: 'Facilities',
                        items: [
                            { name: 'Facility ownership', link: '/battery-ownership', iconClass: 'bi-card-checklist', allowReadOnly: true }
                        ]
                    },
                    {
                        group: 'Tiny Pumper',
                        items: [
                            {
                                name: 'Tiny Pumper',
                                iconClass: 'bi-plugin',
                                link: '/tiny-pumper',
                                permissionKey: PermissionKey.TinyPumper
                            },
                            {
                                name: 'Health & Incidents',
                                link: '/tiny-pumper-health',
                                iconClass: 'bi-activity',
                                permissionKey: PermissionKey.TinyPumper
                            },
                            {
                                name: 'Pending Issues',
                                link: '/tiny-pumper-pending-issues',
                                iconClass: 'bi-exclamation-triangle',
                                permissionKey: PermissionKey.TinyPumper
                            }
                        ]
                    },
                    {
                        group: 'Users',
                        items: [
                            { name: 'Users', link: '/users', iconClass: 'bi-person-lines-fill', allowReadOnly: true },
                            { name: 'Devices by Users', link: '/devices-users', iconClass: 'bi-person-badge', allowReadOnly: true },
                            { name: 'SMS Subscriptions', link: '/sms-subscriptions', iconClass: 'bi-chat-dots', allowReadOnly: true },
                            { name: 'SMS Lookup', link: '/sms-lookup', iconClass: 'bi-search', allowReadOnly: true },
                            {
                                name: 'Admin Users',
                                link: '/users-admin',
                                iconClass: 'bi-person-fill-gear',
                                permissionKey: PermissionKey.UserAdministrator
                            }
                        ]
                    },
                    {
                        group: 'System',
                        items: [
                            { name: 'System Status', link: '/system-status', iconClass: 'bi-activity', allowReadOnly: true },
                            { name: 'Integrations', link: '/integrations', iconClass: 'bi-plug', allowReadOnly: true },
                            { name: 'Mobile diagnostics', link: '/diagnostics', iconClass: 'bi-phone', allowReadOnly: true },
                            { name: 'Ops change inbox', link: '/ops-change-inbox', iconClass: 'bi-envelope', allowReadOnly: true, permissionKey: PermissionKey.Experimental },
                            { name: 'Gear Up', link: '/onboarding', iconClass: 'bi-file-earmark-arrow-up', permissionKey: PermissionKey.GearUp },
                            {
                                name: 'Request Trace',
                                link: '/request-trace',
                                iconClass: 'bi-search',
                                allowReadOnly: true,
                                permissionKey: PermissionKey.Experimental
                            }
                        ]
                    }
                ];

                return menuItems
                    .map(menuItem => ({
                        ...menuItem,
                        items: menuItem.items.filter(item => navigationService.routeHasPermissions(item.permissionKey, item.allowReadOnly))
                    }))
                    .filter(menuItem => menuItem.items.length > 0);
            }
        },
        methods: {
            closeSidebar() {
                this.$emit('toggle-sidebar');
            }
        }
    };
</script>

<template>
    <div id="sidebar" class="sidebar">
        <ul class="nav nav-pills flex-column mb-auto">
            <li v-for="(menuItem, i) in getMenusItems" :key="`sidebar-menu-item-${i}`">
                <ul class="nav nav-pills flex-column" :class="i > 0 ? 'mt-3' : ''" style="list-style: none">
                    <li class="nav-item fw-bold">{{ menuItem.group }}</li>
                    <li
                        v-for="(item, j) in menuItem.items"
                        :key="`sidebar-menu-item-${i}-${j}`"
                        class="nav-item"
                        @click="() => closeSidebar()"
                    >
                        <component
                            :is="item.href ? 'a' : 'router-link'"
                            v-bind="item.href ? { href: item.href, target: '_blank' } : { to: item.link }"
                            class="nav-link"
                        >
                            <i :class="`bi ${item.iconClass} me-2`"></i>
                            <span>{{ item.name }}</span>
                        </component>
                    </li>
                </ul>
            </li>
        </ul>

        <div class="copyright-text">&copy; {{ getYear }} - GreaseBook</div>
    </div>
</template>

<style lang="scss">
    @import '@/styles/_gsb-variables.scss';

    $sidebar-bg-color: white;
    $sidebar-text-color: #333;

    .sidebar {
        display: none;
        position: fixed;
        height: 100%;
        z-index: 1050;
        min-width: $sidebar-width;
        background-color: $sidebar-bg-color;
        bottom: 0;
        padding: 1rem;
        top: $header-height;
        border-right: 1px solid #ddd;

        &.opened {
            display: block;
        }

        .nav {
            .nav-link {
                font-size: 16px;
                text-decoration: none !important;

                &:not(.router-link-exact-active) {
                    &:hover span {
                        text-decoration: underline;
                    }
                }

                &:not(.active) {
                    color: $sidebar-text-color;
                }
            }
        }

        .copyright-text {
            color: $sidebar-text-color;
            position: absolute;
            bottom: 15px;
        }

        @media (min-width: 992px) {
            display: block;

            &.opened {
                display: block;
            }

            width: $sidebar-width;
        }
    }
</style>
